import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {APIClient} from '../../../utils/APIClient';
import {isNetworkError, isOk} from '../../../utils/apiResponse';
import {API_URL} from '../../../settings/local_settings';
import {FACEBOOK_CHANNEL} from '../../channels/channels';
import {PAID_MEDIA_CHANNELS, anyCredentialSyncing, connectionsFromResponse} from './platformsModel';
import {announceReconnectNotice, takeDeferredReconnectNotice} from '../connections/reconnectAccountText';

/**
 * Los datos de Conexiones › Plataformas en la 2.1, con TODO lo que hace hoy la clase clásica
 * (`AgencyConnectionsConfigurationContent`), portado y no importado (contrato §2):
 *
 * - la carga de `GET agency/connections/`, con su error y su reintento;
 * - el poll cada 8 s, hasta 5 min, mientras algún acceso esté sincronizando;
 * - `?just_connected=<CANAL>`: si es la primera vez en ese canal, abre «Crear líneas de seguimiento»;
 * - `&reconnect_ad_account_id=`: le reasigna esa cuenta al acceso que devolvió la conexión (`&credential=`)
 *   o, con un backend viejo que no lo devuelve, al más nuevo del canal. Con el backend de la tanda
 *   «Reconexión de pauta» la reasignación ya la hizo él y el param no llega;
 * - el aviso de una reconexión de Meta, que volvió recargando la página (`deferReconnectNotice`);
 * - `?reconnect_pending=FACEBOOK[&reconnect_acc_id=]`: abre el modal de Meta y limpia la URL;
 * - `ERROR_NO_AGENCY`: al alta de agencia.
 *
 * Y las escrituras de la pantalla: renombrar y quitar un acceso, y quitar una tienda.
 *
 * El reparto de errores es el de `LoadErrorState`: la carga de la pantalla lo dice con reintentar;
 * un refresco de fondo (el poll, la relectura después de una acción) que falla conserva lo que ya se
 * ve, en silencio; un gesto del usuario que falla, con un aviso.
 */

export const SYNC_POLL_INTERVAL_MS = 8000;
export const SYNC_POLL_MAX_DURATION_MS = 5 * 60 * 1000;

const CONNECTIONS_URL = 'agency/connections/';

function readParams(search) {
    const params = new URLSearchParams(search || '');
    return {
        justConnected: params.get('just_connected'),
        reconnectAdAccountId: params.get('reconnect_ad_account_id'),
        credentialId: params.get('credential'),
        reconnectPending: params.get('reconnect_pending'),
        reconnectAccId: params.get('reconnect_acc_id'),
    };
}

export function usePlatformsConnections({search = '', pathname = '/agency/conections', navigate} = {}) {
    const {t} = useTranslation();
    const [connections, setConnections] = useState(null);
    // `null` = sin error; un string = la carga de la pantalla falló (convención de `LoadErrorState`).
    const [loadError, setLoadError] = useState(null);
    const [controlLines, setControlLines] = useState({open: false, adAccounts: []});
    const [facebookReconnect, setFacebookReconnect] = useState({open: false, accId: null});

    const aliveRef = useRef(true);
    // ¿El pedido en curso es la CARGA de la pantalla? Sólo esa muestra el error.
    const screenLoadRef = useRef(true);
    const pollTimeoutRef = useRef(null);
    const pollStartedAtRef = useRef(null);
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;
    const tRef = useRef(t);
    tRef.current = t;
    const loadRef = useRef(null);

    const stopPolling = useCallback(() => {
        if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
        pollStartedAtRef.current = null;
    }, []);

    const schedulePollIfNeeded = useCallback((next) => {
        if (!anyCredentialSyncing(next)) {
            stopPolling();
            return;
        }
        if (pollStartedAtRef.current === null) pollStartedAtRef.current = Date.now();
        if (Date.now() - pollStartedAtRef.current > SYNC_POLL_MAX_DURATION_MS) {
            stopPolling();
            return;
        }
        if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = setTimeout(() => {
            pollTimeoutRef.current = null;
            if (aliveRef.current) loadRef.current();
        }, SYNC_POLL_INTERVAL_MS);
    }, [stopPolling]);

    const autoReassign = useCallback((next, channel, adAccountId, credentialId) => {
        const tr = tRef.current;
        if (!PAID_MEDIA_CHANNELS.includes(channel)) return;
        const credentials = next.credentials[channel] || [];
        if (credentials.length === 0) return;
        const target = credentials.find((credential) => String(credential.id) === String(credentialId))
            || credentials.reduce((best, credential) => (credential.id > best.id ? credential : best));
        new APIClient(`${API_URL}agency/ad-accounts/${encodeURIComponent(adAccountId)}/reassign-credential`)
            .call('POST', {credential_id: target.id})
            .then((data) => {
                // `!isOk`, no `=== 'ERROR'`: un código tipado o un NETWORK_ERROR no pueden festejar.
                if (!isOk(data)) {
                    toast.error(isNetworkError(data)
                        ? tr('reconnect_modal.error_network')
                        : ((data && data.error_message) || tr('reconnect_modal.error_no_access')));
                    return;
                }
                toast.success(tr('reconnect_modal.auto_reassign_success'));
                if (aliveRef.current) loadRef.current();
            })
            .catch(() => toast.error(tr('reconnect_modal.error_no_access')));
    }, []);

    const load = useCallback((reconnectChannel = null, reconnectAdAccountId = null, credentialId = null) => {
        const failed = () => {
            if (aliveRef.current && screenLoadRef.current) setLoadError('load');
        };
        return new APIClient(API_URL + CONNECTIONS_URL).call('GET').then((data) => {
            if (!aliveRef.current) return;
            if (data && data.status === 'ERROR_NO_AGENCY') {
                if (navigateRef.current) navigateRef.current('/agency-register');
                return;
            }
            if (!isOk(data) || !data.data) {
                failed();
                return;
            }
            const next = connectionsFromResponse(data.data);
            screenLoadRef.current = false;
            setConnections(next);
            if (reconnectChannel && reconnectAdAccountId) {
                autoReassign(next, reconnectChannel, reconnectAdAccountId, credentialId);
            }
            schedulePollIfNeeded(next);
        }).catch(failed);
    }, [autoReassign, schedulePollIfNeeded]);
    loadRef.current = load;

    const showControlLinesIfFirstTime = useCallback((channel) => {
        new APIClient(`${API_URL}agency/connections/${encodeURIComponent(channel)}/new/`).call('GET')
            .then((response) => {
                if (!aliveRef.current || !isOk(response) || !response.data) return;
                if (response.data.first_time_on_channel) {
                    setControlLines({open: true, adAccounts: response.data.ad_accounts || []});
                }
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        aliveRef.current = true;
        const params = readParams(search);
        load(params.justConnected, params.reconnectAdAccountId, params.credentialId);
        announceReconnectNotice(takeDeferredReconnectNotice());
        if (params.justConnected) showControlLinesIfFirstTime(params.justConnected);
        // Meta reconecta con el SDK JS de Facebook (popup), no con un redirect de página completa: el
        // «Reconectar» de una pantalla que no puede abrir el modal navega acá con el param. Se limpia
        // la URL para que un F5 no lo reabra. `reconnect_acc_id` puede no venir y es legítimo: sin id,
        // el backend reconecta la conexión de esa persona si existe, o la da de alta.
        if (params.reconnectPending === FACEBOOK_CHANNEL) {
            setFacebookReconnect({open: true, accId: params.reconnectAccId});
            window.history.replaceState({}, '', pathname);
        }
        return () => {
            aliveRef.current = false;
            stopPolling();
        };
        // Es el montaje: los params de la URL se leen una vez, como en el clásico.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Relee en silencio: si falla, queda lo que ya se ve. */
    const refresh = useCallback(() => load(), [load]);

    /** El «Reintentar» del error de carga: vuelve al spinner antes de pedir, así el clic se ve. */
    const retry = useCallback(() => {
        screenLoadRef.current = true;
        setLoadError(null);
        setConnections(null);
        return load();
    }, [load]);

    const closeControlLines = useCallback((count = 0) => {
        setControlLines((prev) => ({...prev, open: false}));
        if (count > 0) toast.success(tRef.current('connections_component.control_lines_created', {count}));
    }, []);

    const openFacebookReconnect = useCallback((accId) => setFacebookReconnect({open: true, accId}), []);
    const closeFacebookReconnect = useCallback(() => setFacebookReconnect({open: false, accId: null}), []);

    /** Una escritura del usuario: avisa si falla, relee si sale bien. Resuelve si salió bien. */
    const write = useCallback((url, method, payload, errorKey) => (
        new APIClient(url).call(method, payload)
            .then((data) => {
                if (!isOk(data)) {
                    toast.error(tRef.current(errorKey));
                    return false;
                }
                if (aliveRef.current) loadRef.current();
                return true;
            })
            .catch(() => {
                toast.error(tRef.current(errorKey));
                return false;
            })
    ), []);

    const renameCredential = useCallback((channel, credentialId, label) => write(
        `${API_URL}agency/credentials/${channel}/${credentialId}/`, 'PATCH', {label},
        'agency_v2.platforms.rename_error',
    ), [write]);

    const removeCredential = useCallback((channel, credentialId) => write(
        `${API_URL}agency/credentials/${channel}/${credentialId}/`, 'DELETE', undefined,
        'agency_v2.platforms.remove_error',
    ), [write]);

    const removeStore = useCallback((adAccountId) => write(
        `${API_URL}agency/ad-accounts/${adAccountId}/disconnect`, 'POST', undefined,
        'agency_v2.platforms.remove_error',
    ), [write]);

    return {
        connections,
        loading: connections === null && loadError === null,
        loadError,
        retry,
        refresh,
        controlLines,
        closeControlLines,
        facebookReconnect,
        openFacebookReconnect,
        closeFacebookReconnect,
        renameCredential,
        removeCredential,
        removeStore,
    };
}
